import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { ShieldAlert } from 'lucide-react';
import { AppShell } from '../components/Layout';
import SeoHead from '../components/seo/SeoHead';

export default function RefundPolicyPage() {
  return (
    <AppShell>
      <SeoHead
        title={"Refund Policy — Blayzrr"} 
        description={"Blayzrr refund policy for one-time audits and monthly subscriptions."} 
        path={"/refund-policy"} 
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <header className="mb-8" data-testid="refund-policy-header">
          <p className="font-mono-edge text-xs tracking-[0.18em] uppercase text-muted-foreground">REFUND POLICY</p>
          <h1 className="font-display text-3xl sm:text-4xl tracking-[-0.01em] mt-1">Refund Policy</h1>
          <p className="text-sm text-muted-foreground mt-2">Last updated: February 2026</p>
        </header>

        <Alert className="mb-6" data-testid="refund-policy-callout">
          <ShieldAlert size={16} />
          <AlertTitle>24-hour window</AlertTitle>
          <AlertDescription>
            Refund requests must be submitted within 24 hours of signup, purchase, or subscription
            activation, from the same email used to register.
          </AlertDescription>
        </Alert>

        <Card>
          <CardContent className="p-6 sm:p-8 space-y-6 leading-relaxed text-sm sm:text-base">
            <p>Blayzrr is committed to customer satisfaction.</p>

            <section>
              <h2 className="font-display text-xl sm:text-2xl mb-2">1. Refund Eligibility</h2>
              <p>You may request a refund if:</p>
              <ul className="list-disc pl-5 space-y-1 mt-2">
                <li>You are dissatisfied with the Service.</li>
                <li>The refund request is submitted within 24 hours of signup, purchase, or subscription activation.</li>
                <li>The request is submitted from the same email address used during registration.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-xl sm:text-2xl mb-2">2. How to Request a Refund</h2>
              <p>Send your request to <a className="underline hover:text-foreground" href="mailto:help@blayzrr.com">help@blayzrr.com</a>.</p>
              <p className="mt-2">Subject line: <span className="font-mono-edge">Refund Request – Blayzrr</span></p>
              <p className="mt-2">Please include:</p>
              <ul className="list-disc pl-5 space-y-1 mt-2">
                <li>Full Name</li>
                <li>Registered Email Address</li>
                <li>Date of Purchase</li>
                <li>Reason for Refund Request</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-xl sm:text-2xl mb-2">3. Refund Review</h2>
              <p>All refund requests are reviewed individually. We reserve the right to deny refunds where:</p>
              <ul className="list-disc pl-5 space-y-1 mt-2">
                <li>More than 24 hours have passed since signup or purchase.</li>
                <li>Fraudulent activity is suspected.</li>
                <li>Excessive or abusive use of the platform is detected.</li>
                <li>Multiple refund requests have been submitted by the same user.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-xl sm:text-2xl mb-2">4. Subscription Cancellation</h2>
              <p>Cancelling a subscription prevents future billing but does not automatically entitle the user to a refund.</p>
            </section>

            <section>
              <h2 className="font-display text-xl sm:text-2xl mb-2">5. Refund Processing</h2>
              <p>Approved refunds are generally processed within 5–7 business days. Actual processing times may vary depending on payment providers and financial institutions.</p>
            </section>

            <section>
              <h2 className="font-display text-xl sm:text-2xl mb-2">6. Payment Processors</h2>
              <p>Payments may be processed through Razorpay or other authorized payment providers. Blayzrr does not store payment card or banking information.</p>
            </section>

            <section>
              <h2 className="font-display text-xl sm:text-2xl mb-2">7. Policy Changes</h2>
              <p>We reserve the right to modify this Refund Policy at any time.</p>
            </section>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
